import { useCallback, useEffect, useId, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams, Link as RouterLink } from 'react-router-dom'
import {
    Box, Typography, Button, Paper, Alert, AlertTitle, Link, CircularProgress,
    Table, TableHead, TableBody, TableRow, TableCell, Chip, ButtonBase,
    RadioGroup, Radio, FormControlLabel,
} from '@mui/material'
import { fieldLabel } from '../photo-upload/ProductFields'
import { slotDisplayName } from '../photo-upload/PhotoUpload'
import { imageUrl } from './reviewModel'
import { formatPrice, formatFieldValue, storeAddress } from './publishModel'
import { getPiecePreview, approveAndPublish, sendBackPiece } from './publishApi'
import { unapprove } from './photoReviewApi'
import RepublishNotice from './RepublishNotice'

// The store page is white: the photos are transparent PNGs that sit on the
// site's white. Its ink and rules are kept apart from the dashboard theme so
// the frame reads as the website, not as another dashboard card.
const STORE = { page: '#ffffff', ink: '#22201d', muted: '#6d6862', rule: '#e7e3dd', bar: '#f4f3f1' }
// The one accent: the mark on a field the website shows. Gold, for a jeweller.
const ON_SITE = '#a8864f'

const photoSrc = photo => imageUrl(photo.photoId, 'edited', photo.attempts, 'preview')

function StorePage({ preview }) {
    const { t } = useTranslation()
    const [selected, setSelected] = useState(0)
    const main = preview.photos[selected] ?? preview.photos[0]
    const price = formatPrice(preview.price)
    const address = storeAddress(preview)

    return (
        <Paper variant='outlined' data-testid='store-page' style={{ backgroundColor: STORE.page }} sx={{ color: STORE.ink, borderColor: STORE.rule, overflow: 'hidden' }}>
            {address && (
                <Box sx={{ bgcolor: STORE.bar, borderBottom: `1px solid ${STORE.rule}`, px: 2, py: 0.75 }}>
                    <Typography variant='caption' sx={{ color: STORE.muted }}>{address}</Typography>
                </Box>
            )}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1.15fr) minmax(0, 1fr)' }, gap: { xs: 3, md: 5 }, p: { xs: 2, md: 4 } }}>
                <Box>
                    <Box sx={{ aspectRatio: '1', bgcolor: STORE.page }}>
                        {main && <img src={photoSrc(main)} alt={main.alt ?? ''} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />}
                    </Box>
                    {preview.photos.length > 1 && (
                        <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
                            {preview.photos.map((photo, index) => (
                                <ButtonBase
                                    key={photo.photoId}
                                    onClick={() => setSelected(index)}
                                    aria-pressed={index === selected}
                                    sx={{
                                        width: 64, height: 64, bgcolor: STORE.page,
                                        border: `1px solid ${index === selected ? STORE.ink : STORE.rule}`,
                                        '&.Mui-focusVisible': { outline: `2px solid ${STORE.ink}`, outlineOffset: 2 },
                                    }}
                                >
                                    <img src={photoSrc(photo)} alt={photo.alt ?? ''} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                                </ButtonBase>
                            ))}
                        </Box>
                    )}
                </Box>
                <Box sx={{ minWidth: 0 }}>
                    <Typography variant='h5' component='h2' sx={{ fontWeight: 400, lineHeight: 1.3 }}>
                        {preview.title?.afterPublish ?? t('photoReview.publish.preview.noTitle')}
                    </Typography>
                    <Typography sx={{ fontSize: '1.15rem', mt: 1.5, mb: 3, color: price ? STORE.ink : STORE.muted }}>
                        {price ?? t('photoReview.publish.preview.noPrice')}
                    </Typography>
                    {preview.description?.afterPublish
                        ? (
                            <Box
                                sx={{ lineHeight: 1.65, maxWidth: '62ch', '& p': { m: 0, mb: 1.5 }, '& ul': { m: 0, pl: 2.5 }, '& li': { mb: 0.5 } }}
                                // The description is the product's own (Shopify) or the
                                // server's template; staff-written either way.
                                dangerouslySetInnerHTML={{ __html: preview.description.afterPublish }}
                            />
                        )
                        : <Typography sx={{ color: STORE.muted }}>{t('photoReview.publish.preview.noDescription')}</Typography>}
                </Box>
            </Box>
        </Paper>
    )
}

// One photo of a gallery: the image on the store's white, its shot's name,
// and what the republish does to it.
function GalleryPhoto({ src, alt, label, mark, removed }) {
    return (
        <Box component='li' sx={{ width: 104 }}>
            <Box sx={{
                width: 104, height: 104, bgcolor: STORE.page, overflow: 'hidden',
                border: theme => (removed ? `2px dashed ${theme.palette.error.main}` : `1px solid ${STORE.rule}`),
            }}>
                <img src={src} alt={alt ?? ''} style={{ width: '100%', height: '100%', objectFit: 'contain', opacity: removed ? 0.45 : 1 }} />
            </Box>
            <Typography variant='body2' sx={{ mt: 0.75, fontWeight: 600, lineHeight: 1.3 }}>{label}</Typography>
            <Box sx={{ mt: 0.5 }}>{mark}</Box>
        </Box>
    )
}

function GalleryColumn({ title, children }) {
    const titleId = useId()
    return (
        <Box sx={{ minWidth: 0 }}>
            <Typography id={titleId} variant='subtitle2' color='text.secondary' sx={{ mb: 1 }}>{title}</Typography>
            <Box component='ul' aria-labelledby={titleId} sx={{ listStyle: 'none', m: 0, p: 0, display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                {children}
            </Box>
        </Box>
    )
}

// A republish sets the whole gallery: what is on the site now, beside what
// it becomes. A new photo is `kept` while it is still the live image.
function GalleryCompare({ gallery }) {
    const { t } = useTranslation()
    const label = slot => (slot ? slotDisplayName(t, slot) : t('photoReview.publish.preview.otherPhoto'))
    // A live photo is labelled by what Daniel sees happen to it: the server's
    // `replaced` covers a slot whose new image is its kept live version too
    // (re-uploaded byte-identical), and that one is Kept on both sides.
    const keptSlots = new Set(gallery.new.filter(photo => photo.change === 'kept').map(photo => photo.slot))
    return (
        <Box>
            <Typography variant='h6' component='h3' sx={{ mb: 1 }}>{t('photoReview.publish.preview.photosTitle')}</Typography>
            {gallery.removedCount > 0 && (
                <Alert severity='warning' sx={{ mb: 2 }}>{t('photoReview.publish.preview.removedCount', { count: gallery.removedCount })}</Alert>
            )}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: { xs: 2.5, md: 4 } }}>
                <GalleryColumn title={t('photoReview.publish.preview.liveGallery')}>
                    {gallery.live.map(media => (
                        <GalleryPhoto
                            key={media.mediaId}
                            src={media.url}
                            alt={media.alt}
                            label={label(media.slot)}
                            removed={media.outcome === 'removed'}
                            mark={media.outcome === 'removed'
                                ? <Typography variant='caption' color='error' sx={{ fontWeight: 600 }}>{t('photoReview.publish.preview.outcome.removed')}</Typography>
                                : keptSlots.has(media.slot)
                                    ? <Chip size='small' variant='outlined' label={t('photoReview.publish.preview.change.kept')} />
                                    : <Typography variant='caption' color='text.secondary'>{t('photoReview.publish.preview.outcome.replaced')}</Typography>}
                        />
                    ))}
                </GalleryColumn>
                <GalleryColumn title={t('photoReview.publish.preview.newGallery')}>
                    {gallery.new.map(photo => (
                        <GalleryPhoto
                            key={photo.photoId}
                            src={photoSrc(photo)}
                            alt={photo.alt}
                            label={label(photo.slot)}
                            mark={photo.change === 'kept'
                                ? <Chip size='small' variant='outlined' label={t('photoReview.publish.preview.change.kept')} />
                                : <Chip size='small' color='primary' label={t('photoReview.publish.preview.change.new')} />}
                        />
                    ))}
                </GalleryColumn>
            </Box>
        </Box>
    )
}

// The live description or the one the template writes from the details;
// Daniel picks, and the page below shows his pick.
// `chosen`: the reviewer's pick, which differs from the preview's own while
// the preview for it failed to load.
function DescriptionChoice({ description, chosen = description.choice, disabled, onChoose }) {
    const { t } = useTranslation()
    const titleId = useId()
    const texts = { live: description.live, generated: description.generated }
    return (
        <Box>
            <Typography id={titleId} variant='h6' component='h3' sx={{ mb: 1 }}>{t('photoReview.publish.preview.descriptionTitle')}</Typography>
            <RadioGroup aria-labelledby={titleId} value={chosen} onChange={e => onChoose(e.target.value)}
                sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.5 }}>
                {(description.choices ?? ['live', 'generated']).map(choice => {
                    const selected = chosen === choice
                    return (
                        <Paper key={choice} variant='outlined' sx={{ p: 1.5, minWidth: 0, borderColor: selected ? 'primary.main' : undefined, boxShadow: theme => (selected ? `inset 0 0 0 1px ${theme.palette.primary.main}` : 'none') }}>
                            <FormControlLabel
                                value={choice}
                                disabled={disabled}
                                control={<Radio />}
                                label={t(`photoReview.publish.preview.descriptionChoice.${choice}`)}
                                sx={{ m: 0, '& .MuiFormControlLabel-label': { fontWeight: selected ? 600 : 400 } }}
                            />
                            {texts[choice]
                                ? (
                                    <Box
                                        sx={{ mt: 1, maxHeight: 220, overflowY: 'auto', fontSize: '0.875rem', lineHeight: 1.6, color: 'text.secondary', '& p': { m: 0, mb: 1 }, '& ul': { m: 0, pl: 2.5 } }}
                                        // The product's own (Shopify) or the server's template; staff-written either way.
                                        dangerouslySetInnerHTML={{ __html: texts[choice] }}
                                    />
                                )
                                : <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>{t(`photoReview.publish.preview.descriptionMissing.${choice}`)}</Typography>}
                        </Paper>
                    )
                })}
            </RadioGroup>
        </Box>
    )
}

function FieldsTable({ fields }) {
    const { t } = useTranslation()
    if (!fields.length) return null
    return (
        <Box>
            <Typography variant='h6' component='h3' sx={{ mb: 1 }}>{t('photoReview.publish.preview.fieldsTitle')}</Typography>
            <Table size='small'>
                <TableHead>
                    <TableRow>
                        <TableCell>{t('photoReview.publish.preview.field')}</TableCell>
                        <TableCell>{t('photoReview.publish.preview.value')}</TableCell>
                        <TableCell>{t('photoReview.publish.preview.onSite')}</TableCell>
                        <TableCell>{t('photoReview.publish.preview.required')}</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {fields.map(field => (
                        <TableRow
                            key={field.key}
                            data-testid={`field-${field.key}`}
                            data-missing={String(field.missing)}
                            sx={field.missing ? { bgcolor: 'rgba(211, 47, 47, 0.08)', '& td:first-of-type': { boxShadow: theme => `inset 3px 0 0 ${theme.palette.error.main}` } } : undefined}
                        >
                            <TableCell>{fieldLabel(t, field.key)}</TableCell>
                            <TableCell>
                                {field.missing
                                    ? <Chip size='small' color='error' label={t('photoReview.publish.preview.missing')} />
                                    : formatFieldValue(field)}
                            </TableCell>
                            <TableCell>
                                {field.onSite && (
                                    <Box component='span' aria-label={t('photoReview.publish.preview.onSite')} role='img'
                                        sx={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', bgcolor: ON_SITE }} />
                                )}
                            </TableCell>
                            <TableCell>{field.required ? t('photoReview.publish.preview.required') : ''}</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </Box>
    )
}

// The in-page "are you sure" for a send-back (the viewer has no confirm()).
function SendBackConfirm({ title, body, busy, onCancel, onConfirm }) {
    const { t } = useTranslation()
    const titleId = useId()
    return (
        <Paper variant='outlined' role='region' aria-labelledby={titleId} sx={{ p: 2, borderColor: 'warning.main', maxWidth: 560 }}>
            <Typography id={titleId} sx={{ fontWeight: 600 }}>{title}</Typography>
            <Typography variant='body2' sx={{ mt: 0.5, mb: 2 }}>{body}</Typography>
            <Box sx={{ display: 'flex', gap: 1.5 }}>
                <Button onClick={onCancel} disabled={busy}>{t('photoReview.publish.preview.cancel')}</Button>
                <Button variant='contained' color='warning' onClick={onConfirm} disabled={busy}
                    startIcon={busy ? <CircularProgress size={16} color='inherit' /> : null}>
                    {t('photoReview.publish.preview.sendBackConfirm')}
                </Button>
            </Box>
        </Paper>
    )
}

// Each shot that publishing would use, with its own way back to review.
function ShotList({ photos, confirming, busy, onAsk, onCancel, onConfirm }) {
    const { t } = useTranslation()
    const titleId = useId()
    return (
        <Box>
            <Typography id={titleId} variant='h6' component='h3' sx={{ mb: 1 }}>{t('photoReview.publish.preview.shotsTitle')}</Typography>
            <Box component='ul' aria-labelledby={titleId} sx={{ listStyle: 'none', m: 0, p: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
                {photos.map(photo => {
                    const label = slotDisplayName(t, photo.slot)
                    return (
                        <Box component='li' key={photo.photoId} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Box sx={{ width: 48, height: 48, bgcolor: STORE.page, border: `1px solid ${STORE.rule}`, flexShrink: 0 }}>
                                    <img src={photoSrc(photo)} alt='' style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                                </Box>
                                <Typography sx={{ flex: 1, minWidth: 0 }}>{label}</Typography>
                                <Button size='small' variant='outlined' color='warning' disabled={busy || Boolean(confirming)} onClick={() => onAsk(photo)}>
                                    {t('photoReview.publish.preview.sendBack')}
                                </Button>
                            </Box>
                            {confirming?.photoId === photo.photoId && (
                                <SendBackConfirm
                                    title={t('photoReview.publish.preview.sendBackPhotoTitle', { label })}
                                    body={t('photoReview.publish.preview.sendBackPhotoBody')}
                                    busy={busy}
                                    onCancel={onCancel}
                                    onConfirm={onConfirm}
                                />
                            )}
                        </Box>
                    )
                })}
            </Box>
        </Box>
    )
}

// One piece's product page as publishing will leave it, and the one button
// that puts it live. The server decides everything shown here (finalize's own
// plan); this page only lays it out. `canPublish`: the user has the admin
// perms the approve route needs; without them the button is not shown.
export default function PiecePreview({ fetchResource, onPublished, onSentBack, onReopenCancelled, canPublish = false }) {
    const { t } = useTranslation()
    const { sku } = useParams()
    const confirmTitleId = useId()
    const [preview, setPreview] = useState(null)
    const [loadError, setLoadError] = useState(null)
    const [alreadyLive, setAlreadyLive] = useState(false)
    const [confirming, setConfirming] = useState(false)
    const [publishing, setPublishing] = useState(false)
    const [published, setPublished] = useState(null)
    const [failure, setFailure] = useState(null)
    const [changed, setChanged] = useState(false)
    // Blockers the server found when approving (a field cleared since the preview).
    const [lateBlockers, setLateBlockers] = useState([])
    const busy = useRef(false)
    // A republish's description choice, kept across reloads (a 409 re-read).
    const choiceRef = useRef(undefined)
    const [choosing, setChoosing] = useState(false)
    // The preview for a new choice could not be read: the one on screen is
    // for the other description, so it cannot be approved.
    const [choiceError, setChoiceError] = useState(null)
    // Send-back: what is being confirmed ({ piece: true } or a photo), the
    // request running, its refusal, and the shots sent back once it worked.
    const [sendBackAsk, setSendBackAsk] = useState(null)
    const [sendingBack, setSendingBack] = useState(false)
    const [sendBackError, setSendBackError] = useState(null)
    const [sentBack, setSentBack] = useState(null)

    const load = useCallback(async () => {
        setLoadError(null)
        const response = await getPiecePreview(fetchResource, sku, choiceRef.current)
        if (response.published) {
            setAlreadyLive(true)
            return
        }
        if (response.error) {
            setLoadError(response.error)
            return response.error
        }
        setLateBlockers([])
        setPreview(response)
        return null
    }, [fetchResource, sku])

    useEffect(() => { load() }, [load])

    // The preview (and its version) is the server's for each choice.
    async function handleChoose(choice) {
        if (busy.current) return
        choiceRef.current = choice
        setConfirming(false)
        setChoosing(true)
        setChoiceError(null)
        const error = await load()
        setChoiceError(error)
        setChoosing(false)
    }

    async function handlePublish() {
        if (busy.current) return
        busy.current = true
        setPublishing(true)
        setFailure(null)
        setChanged(false)
        // A republish names the description its version was hashed for.
        const description = preview.republish ? (preview.description?.choice ?? 'live') : undefined
        const response = await approveAndPublish(fetchResource, sku, preview.version, description)
        setPublishing(false)
        setConfirming(false)
        busy.current = false
        if (!response.error) {
            setPublished({ onlineStoreUrl: response.onlineStoreUrl ?? preview.onlineStoreUrl ?? null })
            onPublished?.()
        } else if (response.version) {
            setChanged(true)
            load()
        } else if (response.blockers?.length) {
            setLateBlockers(response.blockers)
        } else {
            setFailure(response.error)
        }
    }

    // The piece is no longer ready once a shot is back in review, so it
    // leaves "Ready to publish"; the queue is refreshed so its review opens.
    async function handleSendBack() {
        if (busy.current || !sendBackAsk) return
        busy.current = true
        setSendingBack(true)
        setSendBackError(null)
        const response = sendBackAsk.piece
            ? await sendBackPiece(fetchResource, sku)
            : await unapprove(fetchResource, sendBackAsk.photoId)
        setSendingBack(false)
        setSendBackAsk(null)
        busy.current = false
        if (response.error) {
            setSendBackError(response.error)
            return
        }
        await onSentBack?.()
        setSentBack({ count: response.photoIds?.length ?? 1 })
    }

    const back = <Link component={RouterLink} to='/photo-review/publish'>← {t('photoReview.publish.preview.back')}</Link>

    if (alreadyLive) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {back}
                <Alert severity='info'>{t('photoReview.publish.preview.alreadyPublished', { sku })}</Alert>
            </Box>
        )
    }
    if (loadError && !preview) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2 }}>
                {back}
                <Typography color='text.secondary'>{t('photoReview.publish.preview.loadFailed', { error: loadError })}</Typography>
                <Button variant='outlined' onClick={load}>{t('photoReview.publish.preview.tryAgain')}</Button>
            </Box>
        )
    }
    if (!preview) return <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>{back}<CircularProgress size={24} /></Box>
    if (sentBack) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {back}
                <Alert
                    severity='success'
                    action={(
                        <Button color='inherit' size='small' component={RouterLink} to={`/photo-review/${preview.sku}`}>
                            {t('photoReview.publish.preview.openReview')}
                        </Button>
                    )}
                >
                    {t('photoReview.publish.preview.sentBack', { count: sentBack.count, sku: preview.sku })}
                </Alert>
            </Box>
        )
    }

    const blockers = [...preview.blockers, ...lateBlockers.filter(blocker => !preview.blockers.includes(blocker))]
    const canApprove = preview.ready && blockers.length === 0 && !publishing
    const approvalFailed = preview.approval?.approved && preview.publication?.error
    // The server says whether it would refuse (a piece publishing or live).
    const canSendBack = preview.canSendBack === true && !published && !publishing
    const republishing = Boolean(preview.republish)
    const photoCount = preview.gallery?.new?.length ?? preview.photos.length

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pb: 4 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: 2 }}>
                {back}
                <Typography variant='h6' component='span'>{preview.sku}</Typography>
                {preview.category && <Typography component='span' color='text.secondary'>{preview.category}</Typography>}
                {canSendBack && (
                    <Button variant='outlined' color='warning' sx={{ ml: 'auto' }} disabled={sendingBack || Boolean(sendBackAsk)}
                        onClick={() => setSendBackAsk({ piece: true })}>
                        {t('photoReview.publish.preview.sendBackPiece')}
                    </Button>
                )}
            </Box>

            {sendBackAsk?.piece && (
                <SendBackConfirm
                    title={t('photoReview.publish.preview.sendBackPieceTitle', { sku: preview.sku })}
                    body={t('photoReview.publish.preview.sendBackPieceBody')}
                    busy={sendingBack}
                    onCancel={() => setSendBackAsk(null)}
                    onConfirm={handleSendBack}
                />
            )}
            {sendBackError && (
                <Alert severity='error' onClose={() => setSendBackError(null)}>{t('photoReview.publish.preview.sendBackFailed', { error: sendBackError })}</Alert>
            )}

            {republishing && <RepublishNotice sku={preview.sku} republish={preview.republish} fetchResource={fetchResource} onCancelled={onReopenCancelled} />}

            {blockers.length > 0 && (
                <Alert severity='error' variant='filled'>
                    <AlertTitle>{t('photoReview.publish.preview.blockersTitle')}</AlertTitle>
                    <Box component='ul' sx={{ m: 0, pl: 2.5 }}>
                        {blockers.map(blocker => <li key={blocker}>{blocker}</li>)}
                    </Box>
                </Alert>
            )}
            {changed && <Alert severity='info' onClose={() => setChanged(false)}>{t('photoReview.publish.preview.changed')}</Alert>}
            {failure && <Alert severity='error' onClose={() => setFailure(null)}>{t('photoReview.publish.preview.failed', { error: failure })}</Alert>}
            {approvalFailed && !published && !failure && (
                <Alert severity='warning'>{t('photoReview.publish.preview.approvedBy', { name: preview.approval.approvedBy, error: preview.publication.error })}</Alert>
            )}
            {published && (
                <Alert
                    severity='success'
                    action={published.onlineStoreUrl && (
                        <Button color='inherit' size='small' component='a' href={published.onlineStoreUrl} target='_blank' rel='noreferrer'>
                            {t('photoReview.publish.preview.viewOnSite')}
                        </Button>
                    )}
                >
                    {t(republishing ? 'photoReview.publish.preview.republished' : 'photoReview.publish.preview.published', { sku: preview.sku })}
                </Alert>
            )}

            {preview.ready && (
                <>
                    {preview.gallery && <GalleryCompare gallery={preview.gallery} />}
                    {republishing && preview.description && (
                        <DescriptionChoice description={preview.description} chosen={choiceError ? choiceRef.current : undefined} disabled={choosing || publishing} onChoose={handleChoose} />
                    )}
                    {choiceError && (
                        <Alert severity='error' action={(
                            <Button color='inherit' size='small' disabled={choosing} onClick={() => handleChoose(choiceRef.current)}>
                                {t('photoReview.publish.preview.tryAgain')}
                            </Button>
                        )}>
                            {t('photoReview.publish.preview.choiceFailed', { error: choiceError })}
                        </Alert>
                    )}
                    <StorePage preview={preview} />
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, color: 'text.secondary' }}>
                        {preview.description && !republishing && (
                            <Typography variant='body2'>
                                {preview.description.willWrite
                                    ? t('photoReview.publish.preview.descriptionGenerated', { category: preview.category })
                                    : t('photoReview.publish.preview.descriptionKept')}
                            </Typography>
                        )}
                        {preview.title?.fromFields && (
                            <Typography variant='body2'>{t('photoReview.publish.preview.titleFromFields', { title: preview.title.fromFields })}</Typography>
                        )}
                        {preview.title && !preview.online && <Typography variant='body2'>{t('photoReview.publish.preview.notOnline')}</Typography>}
                    </Box>
                    <FieldsTable fields={preview.fields} />
                    {canSendBack && preview.photos.length > 0 && (
                        <ShotList
                            photos={preview.photos}
                            confirming={sendBackAsk?.piece ? null : sendBackAsk}
                            busy={sendingBack}
                            onAsk={photo => setSendBackAsk({ photoId: photo.photoId })}
                            onCancel={() => setSendBackAsk(null)}
                            onConfirm={handleSendBack}
                        />
                    )}
                </>
            )}

            {!published && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {canPublish && blockers.length === 0 && preview.ready && !confirming && (
                        <Typography color='text.secondary'>{t('photoReview.publish.preview.readyLine')}</Typography>
                    )}
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                        <Button variant='outlined' component={RouterLink} to={`/photo-upload?sku=${preview.sku}`}>
                            {t('photoReview.publish.preview.fix')}
                        </Button>
                        {canPublish && (
                            <Button variant='contained' color='success' disabled={!canApprove || confirming || choosing || Boolean(choiceError)} onClick={() => setConfirming(true)}>
                                {t(republishing ? 'photoReview.publish.preview.approveRepublish' : 'photoReview.publish.preview.approve')}
                            </Button>
                        )}
                    </Box>
                    {canPublish && confirming && (
                        <Paper variant='outlined' role='region' aria-labelledby={confirmTitleId} sx={{ p: 2, borderColor: 'success.main', maxWidth: 560 }}>
                            <Typography id={confirmTitleId} sx={{ fontWeight: 600 }}>{t(republishing ? 'photoReview.publish.preview.confirmRepublishTitle' : 'photoReview.publish.preview.confirmTitle', { sku: preview.sku })}</Typography>
                            <Typography variant='body2' sx={{ mt: 0.5, mb: republishing && preview.gallery?.removedCount > 0 ? 0.5 : 2 }}>{t('photoReview.publish.preview.confirmBody', { count: photoCount })}</Typography>
                            {republishing && preview.gallery?.removedCount > 0 && (
                                <Typography variant='body2' sx={{ mb: 2 }}>{t('photoReview.publish.preview.removedCount', { count: preview.gallery.removedCount })}</Typography>
                            )}
                            <Box sx={{ display: 'flex', gap: 1.5 }}>
                                <Button onClick={() => setConfirming(false)} disabled={publishing}>{t('photoReview.publish.preview.cancel')}</Button>
                                <Button variant='contained' color='success' onClick={handlePublish} disabled={publishing}
                                    startIcon={publishing ? <CircularProgress size={16} color='inherit' /> : null}>
                                    {publishing ? t('photoReview.publish.preview.publishing') : t(republishing ? 'photoReview.publish.preview.confirmRepublish' : 'photoReview.publish.preview.confirm')}
                                </Button>
                            </Box>
                        </Paper>
                    )}
                </Box>
            )}
        </Box>
    )
}
